import { GetDPSDetailResponse, GetPPDetailResponse, QuestionnaireConfig, QuestionnaireEvaluateResult } from "@repo/api";
import {
    FundsRow,
    selectExistingFundsRows,
    selectNewFundsRows,
    StepperType,
    stepperTypeSelector,
    useAppSelector,
    whatToChangeAnswersSelector,
} from "@repo/logic";
import { useMemo } from "react";
import { useTypedTranslation } from "../../../hooks/useTypedTranslation";
import { CurrentSituation } from "../Questionnaire.types";
import { mapCurrentAllocationsFromDetail } from "../Questionnaire/QuestionnairePage.helpers";
import { EditInvestmentStrategyMode } from "./EditInvestmentStrategy.types";

export const useCalculatedData = (
    pensionDetail: GetDPSDetailResponse | GetPPDetailResponse | undefined,
    config: QuestionnaireConfig | undefined,
    mode: EditInvestmentStrategyMode,
    investmentProfile: QuestionnaireEvaluateResult | null | undefined,
    reduxExistingFundRows: FundsRow[] | undefined,
    reduxNewFundRows: FundsRow[] | undefined,
) => {
    const currentSituationMemoized = useMemo<CurrentSituation | undefined>(() => {
        return (pensionDetail as GetDPSDetailResponse | undefined)?.funds
            ? mapCurrentAllocationsFromDetail(pensionDetail as GetDPSDetailResponse, config, mode)
            : undefined;
    }, [config, mode, pensionDetail]);

    const data = useMemo(() => {
        const recommendedProfile = config?.investmentProfiles?.find(
            (profile) => profile.id === investmentProfile?.profileId,
        );
        const recommendedStrategy = config?.recommendedStrategies?.find(
            (strategy) => strategy.id === investmentProfile?.recommendedStrategyId,
        );
        const fundsWithAllocationRecommendations = config?.funds?.map((fund) => {
            const recommendedFund = recommendedStrategy?.funds.find((f) => f.id === fund.id);
            return {
                ...fund,
                recommendedPercentageAllocation: recommendedFund?.recommendedPercentageAllocation ?? 0,
                maxPercentageAllocation: recommendedFund?.maxPercentageAllocation ?? 0,
            };
        });

        const reduxRows = mode === "existing" ? reduxExistingFundRows : reduxNewFundRows;
        const currentRows: FundsRow[] =
            currentSituationMemoized?.currentAllocations.map((allocation) => {
                return {
                    id: allocation.fundId,
                    value: allocation.percentage,
                };
            }) ?? [];

        // If there are rows already dispatched to redux (existing/new), prefer showing them in the progress bars.
        const preferredSituation = reduxRows && reduxRows.length > 0 ? reduxRows : currentRows;
        const progressBarRows = preferredSituation?.map((r) => {
            const fund = config?.funds.find((f) => f.id === r.id);
            return {
                id: r.id,
                label: fund?.name ?? "",
                value: r.value,
            };
        });
        return {
            progressBarRows,
            fundsWithAllocationRecommendations,
            recommendedProfile,
            recommendedStrategy,
            preferredSituation,
        };
    }, [currentSituationMemoized, investmentProfile, config, reduxExistingFundRows, reduxNewFundRows, mode]);

    return { currentSituationMemoized, data };
};

export const useFundsChanged = (
    pensionDetail: GetDPSDetailResponse | GetPPDetailResponse | undefined,
    config: QuestionnaireConfig | undefined,
    mode: EditInvestmentStrategyMode,
    currentFormsFunds: { id: number; allocation: string }[],
) => {
    const whatToChangeAnswers = useAppSelector(whatToChangeAnswersSelector);
    const existingBefore = mapCurrentAllocationsFromDetail(pensionDetail as GetDPSDetailResponse, config, "existing");
    const newBefore = mapCurrentAllocationsFromDetail(pensionDetail as GetDPSDetailResponse, config, "new");
    const reduxExistingFundRows = useAppSelector(selectExistingFundsRows);
    const reduxNewFundRows = useAppSelector(selectNewFundsRows);

    return useMemo(() => {
        // Normalize allocations into comparable sorted arrays
        const normalizeAllocations = (
            rows?: Array<{
                id: number;
                allocation?: string | number;
                value?: number;
            }>,
        ) =>
            (rows ?? [])
                .map(({ id, allocation, value }) => ({ id, value: Number(allocation ?? value ?? 0) }))
                .sort((a, b) => a.id - b.id);

        // Shallow equality check on normalized allocations
        const allocationsEqual = (first: { id: number; value: number }[], second: { id: number; value: number }[]) =>
            first.length === second.length &&
            first.every((row, index) => row.id === second[index].id && row.value === second[index].value);

        // Baseline from API detail (before user edits)
        const existingBeforeAllocations = normalizeAllocations(
            existingBefore?.currentAllocations?.map((allocation) => ({
                id: allocation.fundId,
                allocation: allocation.percentage,
            })),
        );

        const newBeforeAllocations = normalizeAllocations(
            newBefore?.currentAllocations?.map((allocation) => ({
                id: allocation.fundId,
                allocation: allocation.percentage,
            })),
        );

        // Current candidate values: form funds in the active mode, otherwise Redux snapshot
        const existingCurrentAllocations = normalizeAllocations(
            mode === "existing" ? currentFormsFunds : (reduxExistingFundRows ?? existingBeforeAllocations),
        );

        const newCurrentAllocations = normalizeAllocations(
            mode === "new" ? currentFormsFunds : (reduxNewFundRows ?? newBeforeAllocations),
        );

        return {
            existingWillChange: whatToChangeAnswers?.existingContribution ?? false,
            existingChanged: !allocationsEqual(existingCurrentAllocations, existingBeforeAllocations),
            newWillChange: whatToChangeAnswers?.newContribution ?? false,
            newChanged: !allocationsEqual(newCurrentAllocations, newBeforeAllocations),
        };
    }, [
        currentFormsFunds,
        existingBefore,
        mode,
        newBefore,
        reduxExistingFundRows,
        reduxNewFundRows,
        whatToChangeAnswers?.existingContribution,
        whatToChangeAnswers?.newContribution,
    ]);
};

export const useNoChangeWarning = (
    mode: EditInvestmentStrategyMode,
    changes: {
        existingWillChange: boolean;
        existingChanged: boolean;
        newWillChange: boolean;
        newChanged: boolean;
    },
) => {
    const stepperType = useAppSelector(stepperTypeSelector);
    const messages = useMessages(stepperType);

    const warning = useMemo(() => {
        if (
            (stepperType === StepperType.PensionQuestionnaire ||
                stepperType === StepperType.ChangePensionContribution) &&
            mode === "existing" &&
            !changes.existingChanged
        ) {
            // Staying on existing step, no change here; allow continue if new contributions will be changed next
            if (changes.newWillChange) {
                return messages.noChangeContinue;
            }
            // No change anywhere → inform and exit or skip to recap
            return messages.noChangeBlocker;
        }

        if (
            (stepperType === StepperType.PensionQuestionnaire ||
                stepperType === StepperType.ChangePensionContribution) &&
            mode === "new" &&
            !changes.newChanged
        ) {
            // On new step, but existing already changed earlier → allow continue
            if (changes.existingChanged) {
                return messages.noChangeContinue;
            }
            // No change in new and no prior existing change → exit or skip to recap
            return messages.noChangeBlocker;
        }

        return null;
    }, [changes.existingChanged, changes.newChanged, changes.newWillChange, mode, stepperType, messages]);

    const shouldExitOnNext = useMemo(() => {
        if (stepperType === StepperType.PensionQuestionnaire) {
            if (
                (mode === "existing" && !changes.existingChanged && !changes.newWillChange) ||
                (mode === "new" && !changes.newChanged && !changes.existingChanged)
            ) {
                return true;
            }
        }
        return false;
    }, [changes.existingChanged, changes.newChanged, changes.newWillChange, mode, stepperType]);

    const shouldSkipToRecap = useMemo(() => {
        if (stepperType === StepperType.ChangePensionContribution) {
            if (
                (mode === "existing" && !changes.existingChanged && !changes.newWillChange) ||
                (mode === "new" && !changes.newChanged && !changes.existingChanged)
            ) {
                return true;
            }
        }
        return false;
    }, [stepperType, mode, changes.existingChanged, changes.newWillChange, changes.newChanged]);

    return { warning, shouldExitOnNext, shouldSkipToRecap };
};

const useMessages = (stepperType: StepperType | undefined) => {
    const { t } = useTypedTranslation();

    return useMemo(() => {
        if (stepperType === StepperType.PensionQuestionnaire) {
            return {
                noChangeBlocker: t(
                    "investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.noChangeExit",
                ),
                noChangeContinue: t(
                    "investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.noChangeContinue",
                ),
            };
        } else if (stepperType === StepperType.ChangePensionContribution) {
            return {
                noChangeBlocker: t("investmentStrategy.whatToChange.noChangeWarningForChangeContribution"),
                noChangeContinue: t(
                    "investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.noChangeContinue",
                ),
            };
        }
        return {
            noChangeBlocker: "",
            noChangeContinue: "",
        };
    }, [stepperType, t]);
};
